import { Search, X } from 'lucide-react';
import { forwardRef, type InputHTMLAttributes } from 'react';
import { cn } from '@/shared/lib/cn';

export type SearchInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange' | 'type'
> & {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  /** aria-label aplicado tanto ao input quanto ao botao limpar. */
  ariaLabel?: string;
};

// Search bar padrao do app. Fonte unica de verdade: todas as telas com
// busca devem consumir este componente em vez de reimplementar o input.
// Estilo: bg-gray-50 sutil, ring cinza no focus (sem peso), icone lupa
// na esquerda, botao X aparece quando ha texto digitado.
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      value,
      onChange,
      placeholder = 'Buscar',
      className,
      ariaLabel,
      ...rest
    },
    ref,
  ) => {
    const label = ariaLabel ?? placeholder;
    return (
      <div className={cn('relative w-full', className)}>
        <Search
          className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
          aria-hidden
        />
        <input
          ref={ref}
          type="search"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          aria-label={label}
          className={cn(
            'h-12 w-full rounded-xl border border-gray-200 bg-gray-50 pl-11 text-sm text-gray-900 placeholder:text-gray-400 transition-all',
            'focus:border-gray-300 focus:bg-white focus:outline-none focus:ring-2 focus:ring-gray-100',
            value.length > 0 ? 'pr-11' : 'pr-4',
          )}
          {...rest}
        />
        {value.length > 0 ? (
          <button
            type="button"
            onClick={() => onChange('')}
            aria-label={`Limpar ${label.toLowerCase()}`}
            className="absolute right-2.5 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}
      </div>
    );
  },
);

SearchInput.displayName = 'SearchInput';
